import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import {
  Search,
  Filter,
  Download,
  CheckCircle,
  Clock,
  Calendar,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  Trash2,
} from 'lucide-react';
import { ProfessionalPaymentRecord, PeriodType } from '../../types/professional-payment';
import { ProfessionalPaymentRecordService } from '../../services/ProfessionalPaymentRecordService';
import { ProfessionalPaymentPDFService } from '../../services/ProfessionalPaymentPDFService';
import { DeletePaymentModal } from './DeletePaymentModal';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

export function PaymentHistoryTab() {
  const { user, selectedClient } = useAuth();
  const [payments, setPayments] = useState<ProfessionalPaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'paid' | 'pending'>('all');
  const [filterType, setFilterType] = useState<'all' | PeriodType>('all');
  const [expandedPayment, setExpandedPayment] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<ProfessionalPaymentRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exportingPDF, setExportingPDF] = useState<string | null>(null);

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      if (!selectedClient?.clientId) {
        setPayments([]);
        setLoading(false);
        return;
      }

      const filters: any = {
        clientId: selectedClient.clientId
      };

      if (filterStatus !== 'all') {
        filters.paymentIsPaid = filterStatus === 'paid';
      }

      const data = await ProfessionalPaymentRecordService.getPaymentHistory(filters);
      setPayments(data);
    } catch (error) {
      console.error('Error loading payment history:', error);
      toast.error('Erro ao carregar histórico de pagamentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, [filterStatus, filterType, selectedClient?.clientId]);

  const filteredPayments = payments.filter((payment) => {
    const matchesSearch = (payment.professionalName || '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const exportToCSV = (payment: ProfessionalPaymentRecord) => {
    const headers = [
      'Profissional',
      'Período',
      'Tipo',
      'Data do Atendimento',
      'Paciente',
      'Descrição',
      'Local',
      'Tem Evolução',
      'Valor',
    ];

    const rows = (payment.appointmentDetails || []).map((detail) => [
      payment.professionalName,
      payment.reportPeriod,
      payment.reportType === 'billing_period' ? 'Faturamento' : 'Data Fixa',
      detail.date,
      detail.patientName,
      detail.description,
      detail.location,
      detail.hasEvolution ? 'Sim' : 'Não',
      `R$ ${detail.value.toFixed(2)}`,
    ]);

    const totalRow = [
      '',
      '',
      '',
      '',
      '',
      '',
      'TOTAL:',
      `${payment.totalSessions} sessões`,
      `R$ ${payment.totalAmount.toFixed(2)}`,
    ];

    const csvContent = [headers, ...rows, totalRow]
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell || '');
            if (
              cellStr.includes(',') ||
              cellStr.includes('"') ||
              cellStr.includes('\n')
            ) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `pagamento_${payment.professionalName}_${payment.reportPeriod}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleExpand = (paymentId: string) => {
    setExpandedPayment(expandedPayment === paymentId ? null : paymentId);
  };

  const formatDate = (date: Timestamp | Date) => {
    if (date instanceof Timestamp) {
      return format(date.toDate(), 'dd/MM/yyyy');
    }
    return format(date, 'dd/MM/yyyy');
  };

  const handleExportToPDF = async (payment: ProfessionalPaymentRecord) => {
    if (!payment.id) return;

    try {
      setExportingPDF(payment.id);
      await ProfessionalPaymentPDFService.downloadPDF(payment);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setExportingPDF(null);
    }
  };

  const handleDeleteClick = (payment: ProfessionalPaymentRecord) => {
    setPaymentToDelete(payment);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const paymentId = (paymentToDelete as any)?.paymentId || (paymentToDelete as any)?.id;
    if (!paymentId || !user || !selectedClient?.clientId) return;

    try {
      setDeleting(true);
      await ProfessionalPaymentRecordService.deletePaymentRecord(
        paymentId,
        user.userId,
        selectedClient.clientId
      );
      toast.success('Pagamento apagado com sucesso!');
      setDeleteModalOpen(false);
      setPaymentToDelete(null);
      await loadPayments();
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Erro ao apagar pagamento');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar profissional..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Todos os Status</option>
            <option value="paid">Pagos</option>
            <option value="pending">Pendentes</option>
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Todos os Tipos</option>
            <option value="billing_period">Por Faturamento</option>
            <option value="date_range">Por Data Fixa</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredPayments.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>Nenhum registro de pagamento encontrado</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPayments.map((payment) => (
            <div
              key={payment.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {payment.professionalName}
                    </h3>
                    {payment.isPaid ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        <CheckCircle className="w-4 h-4" />
                        Pago
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                        <Clock className="w-4 h-4" />
                        Pendente
                      </span>
                    )}
                    <span className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                      {payment.reportType === 'billing_period'
                        ? 'Faturamento'
                        : 'Data Fixa'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleExportToPDF(payment)}
                      disabled={exportingPDF === payment.id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Exportar PDF"
                    >
                      {exportingPDF === payment.id ? (
                        <>
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                          Gerando...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4" />
                          PDF
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => exportToCSV(payment)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Exportar CSV"
                    >
                      <Download className="w-4 h-4" />
                      CSV
                    </button>

                    <button
                      onClick={() => handleDeleteClick(payment)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      title="Apagar Pagamento"
                    >
                      <Trash2 className="w-4 h-4" />
                      Apagar
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Período</div>
                    <div className="font-medium text-gray-900">
                      {payment.reportPeriod}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-gray-500 mb-1">Sessões</div>
                    <div className="font-medium text-gray-900">
                      {payment.totalSessions}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-gray-500 mb-1">
                      Com Evolução
                    </div>
                    <div className="font-medium text-gray-900">
                      {payment.sessionsWithEvolution}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-gray-500 mb-1">
                      Valor Total
                    </div>
                    <div className="font-medium text-green-600 text-lg">
                      R$ {payment.totalAmount.toFixed(2)}
                    </div>
                  </div>
                </div>

                {payment.isPaid && payment.paidAt && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <div className="text-green-700 font-medium mb-1">
                          Data do Pagamento
                        </div>
                        <div className="text-green-900">
                          {formatDate(payment.paidAt)}
                        </div>
                      </div>
                      {payment.paymentMethod && (
                        <div>
                          <div className="text-green-700 font-medium mb-1">
                            Método
                          </div>
                          <div className="text-green-900 capitalize">
                            {payment.paymentMethod}
                          </div>
                        </div>
                      )}
                      {payment.notes && (
                        <div className="col-span-2 md:col-span-1">
                          <div className="text-green-700 font-medium mb-1">
                            Observações
                          </div>
                          <div className="text-green-900">{payment.notes}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => toggleExpand(payment.id!)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  {expandedPayment === payment.id ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Ocultar Detalhes
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Ver Detalhes dos Atendimentos
                    </>
                  )}
                </button>
              </div>

              {expandedPayment === payment.id && (
                <div className="border-t border-gray-200 bg-gray-50 p-4">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Detalhes dos Atendimentos
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                            Data
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                            Paciente
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                            Local
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                            Evolução
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                            Valor
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(payment.appointmentDetails || []).map((detail, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm text-gray-900">
                              {detail.date}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900">
                              {detail.patientName}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-600">
                              {detail.location}
                            </td>
                            <td className="px-3 py-2">
                              {detail.hasEvolution ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                  <CheckCircle className="w-3 h-3" />
                                  Sim
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                  Não
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-right">
                              R$ {detail.value.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                        {(payment.appointmentDetails || []).length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-3 py-4 text-sm text-gray-500 text-center">
                              Nenhum atendimento vinculado a este pagamento.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {paymentToDelete && (
        <DeletePaymentModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setPaymentToDelete(null);
          }}
          payment={paymentToDelete}
          onConfirm={handleConfirmDelete}
          loading={deleting}
        />
      )}
    </div>
  );
}
